import { Box, Divider, Typography } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import { useAppSelector } from 'store/hook';

import { useMemo } from 'react';
import {
  calculateDowntimeStats,
  formatNumberToShortScale,
} from '../utils/dataFormatters';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { truncateTo2dp } from 'utils/helpers/general';
import { HighestCostingCard } from './HighestCostingCard';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface BarChartDataItem {
  name: string;
  value: number;
  color: string;
}

const DowntimeBarChart = ({ data }: { data: BarChartDataItem[] }) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  return (
    <ResponsiveContainer width={'100%'} height={'100%'}>
      <BarChart
        data={data}
        style={{
          color: palette.textPrimary,
        }}
        barSize={40}
        outerRadius={2}
        margin={{ top: 0, right: 0, left: -20, bottom: -10 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke="#26364C"
        />
        <XAxis
          dataKey="name"
          tick={{
            fill: enableDarkTheme ? '#878A99' : 'grey',
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fontSize: '12px',
          }}
        />
        <YAxis
          tick={{
            fill: enableDarkTheme ? '#878A99' : 'grey',
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fontSize: '12px',
          }}
          tickFormatter={(value: number) => formatNumberToShortScale(value)}
        />
        <Tooltip
          cursor={{
            fill: enableDarkTheme ? '#1B2B4A' : 'rgba(225,225,225,.8)',
          }}
          contentStyle={{
            backgroundColor: enableDarkTheme
              ? palette.toolBarBackground
              : palette.boxBackground,
            border: enableDarkTheme ? '1px solid #2F445D' : '1px solid #BDBDBD',
            borderRadius: '4px',
            color: palette.textPrimary,
          }}
          labelStyle={{
            color: palette.textPrimary,
            fontSize: '14px',
            fontWeight: 600,
          }}
          itemStyle={{
            color: palette.textPrimary,
            fontSize: '12px',
          }}
          formatter={(value: number) => `${truncateTo2dp(value)} hrs`}
        />

        <Bar dataKey="value">
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.color} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export const DowntimeHours = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const currDownTimeData = useAppSelector(
    (state) => state.squaresDashboardReducer?.downTimeData?.current
  );

  const savingsData = useAppSelector(
    (state) => state.squaresDashboardReducer.savings
  );

  const assetsData = useAppSelector((state) => state.assetReducer.assets);

  const aggregatedDowntimeData = useMemo(() => {
    const temp = calculateDowntimeStats(currDownTimeData);
    const assetWithHighestDowntimeInfo = assetsData.find(
      (el) => el.bumperNumber === temp.topAssetByDowntime?.bumper_number
    );
    return {
      ...temp,
      topAssetByDowntime: {
        ...temp.topAssetByDowntime,
        icon:
          assetWithHighestDowntimeInfo?.make &&
          assetWithHighestDowntimeInfo?.model
            ? getImageNameByMakeModelAndAsset(
                assetWithHighestDowntimeInfo?.make,
                assetWithHighestDowntimeInfo?.model,
                assetWithHighestDowntimeInfo?.assetType,
                'Small'
              )
            : null,
        makeModel: `${assetWithHighestDowntimeInfo?.make}-${assetWithHighestDowntimeInfo?.model}`,
      },
    };
  }, [currDownTimeData]);

  const barChartData: BarChartDataItem[] = [
    {
      name: 'PM',
      value: aggregatedDowntimeData.totalPmDuration,
      color: '#A776FF',
    },
    {
      name: 'CM',
      value: aggregatedDowntimeData.totalCmDuration,
      color: '#FFA403',
    },
  ];
  return (
    <Box
      sx={{
        backgroundColor: enableDarkTheme
          ? palette.toolBarBackground
          : palette.toolBarBackground,
        borderRadius: '5px',
        border: enableDarkTheme ? '1px solid #2F445D' : '1px solid #BDBDBD',
      }}
      p={1}
      display={'flex'}
      flexDirection={'column'}
      gap={1}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            color: palette.textPrimary,
            fontWeight: 'bolder',
          }}
        >
          {t('downtime_hours')}
        </Typography>
      </Box>
      <Box display={'flex'} flexDirection={'column'} gap={1}>
        <Box
          display={'flex'}
          flexDirection={'row'}
          gap={1}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
            <Typography
              sx={{
                color: palette.grey,
              }}
              fontSize={'12px'}
            >
              {t('total_hrs')}
            </Typography>
            <Box>
              <Typography
                sx={{
                  color: palette.textPrimary,
                  fontWeight: '700',
                }}
                fontSize={'18px'}
              >
                {`${formatNumberToShortScale(
                  aggregatedDowntimeData.totalCmDuration +
                    aggregatedDowntimeData.totalPmDuration
                )} Hrs`}
              </Typography>
            </Box>
          </Box>

          <Divider
            orientation="vertical"
            sx={{
              height: '20px',
              background: enableDarkTheme ? '#2F445D' : '#9E9E9E',
            }}
          />
          <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
            <Typography
              sx={{
                color: palette.grey,
              }}
              fontSize={'12px'}
            >
              {t('potential_downtime_savings')}
            </Typography>
            <Box>
              <Typography
                sx={{
                  color: palette.textPrimary,
                  fontWeight: '700',
                }}
                fontSize={'18px'}
              >
                {savingsData?.downtimeSaved
                  ? `${truncateTo2dp(savingsData?.downtimeSaved)} hrs`
                  : `0 hrs`}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box width={'100%'} height={'120px'}>
          <DowntimeBarChart data={barChartData} />
        </Box>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: '2px',
            background: enableDarkTheme ? '#2F445D' : '#9E9E9E',
          }}
        />
        {aggregatedDowntimeData.topAssetByDowntime && (
          <HighestCostingCard
            icon={aggregatedDowntimeData.topAssetByDowntime.icon}
            name={aggregatedDowntimeData.topAssetByDowntime.bumper_number ?? ''}
            title={t('asset_with_highest_downtime')}
            subName={aggregatedDowntimeData.topAssetByDowntime.makeModel}
            usePngComponent
            value={`${formatNumberToShortScale(
              (aggregatedDowntimeData.topAssetByDowntime.pmDuration ?? 0) +
                (aggregatedDowntimeData.topAssetByDowntime.cmDuration ?? 0)
            )} Hrs`}
          />
        )}
      </Box>
    </Box>
  );
};
